import {
  Condition,
  ConditionType,
  Entity,
  formatTicker,
  ScenariosCondition,
} from '@toggle/toggle';
import { t, TFunction } from 'i18next';
import { divide } from 'number-precision';

import { FilterOption } from '~/components/update-filter-options/updateFilterOptions';

export const DECIMAL_TO_PERCENTAGE_FACTOR = 100;
export const CUSTOM = 'custom';

export const formatScenarioConditionValue = <T extends ScenariosCondition>(
  condition: T
) => {
  const { value } = condition;
  const isValidConditionType =
    condition.condition === ConditionType.RisesBy ||
    condition.condition === ConditionType.FallsBy;

  const isNumber = typeof value === 'number' && isFinite(value);
  const isDecimalValue = isNumber && value % 1 !== 0;
  const shouldDivide = isValidConditionType && isNumber && !isDecimalValue;

  if (shouldDivide) {
    return {
      ...condition,
      value: divide(value, DECIMAL_TO_PERCENTAGE_FACTOR),
    };
  }

  return condition;
};

export const generateConditionString = <T extends Condition>({
  t,
  conditions,
}: {
  t: TFunction;
  conditions?: T[];
}): string => {
  if (!conditions) {
    return '';
  }

  const formatCondition = ({
    condition,
    duration,
    duration_unit,
    value,
    max_value,
    min_value,
    entityName,
    formattedTicker,
    snakeName,
  }: Condition): string => {
    /* i18next-extract-mark-context-next-line  ["d", "w", "m", "y"] */
    const period = t(`scenarioCommander:domino.period`, {
      context: duration_unit,
      count: duration,
    });

    /* i18next-extract-mark-context-next-line  ["above", "below", "between", "crosses_above", "crosses_below", "falls_by", "falls_by_level", "rises_by", "rises_by_level"] */
    return t(`scenarioCommander:domino.conditions`, {
      context: condition,
      entityName,
      formattedTicker: `(${formattedTicker})`,
      snakeName,
      duration,
      period,
      value,
      max_value,
      min_value,
    });
  };

  const andLabel = t('scenarioCommander:domino.and');
  return conditions.map(formatCondition).join(`${andLabel}\n`);
};

export const updateConditionWithEntityDetails = (
  conditions: Condition[],
  entities?: Entity[]
): Condition[] =>
  conditions.map(condition => {
    const matchingEntity = entities?.find(
      entity => entity.tag === condition.entity
    );

    if (!matchingEntity) {
      return condition;
    }

    return {
      ...condition,
      entityName: matchingEntity.name,
      formattedTicker: formatTicker(matchingEntity),
    };
  });

const formatInterpolations = (selectedOption?: FilterOption) => {
  if (!selectedOption) {
    return '';
  }

  const { value: conditionObj } = selectedOption;
  const {
    value,
    duration_unit: durationUnit,
    max_value: maxValue,
    min_value: minValue,
    duration,
  } = conditionObj as Condition;

  return {
    value,
    max_value: maxValue,
    min_value: minValue,
    period:
      durationUnit && duration
        ? /* i18next-extract-mark-context-next-line  ["d", "w", "m", "y"] */
          t(`scenarioCommander:domino.period`, {
            context: durationUnit,
            count: duration,
          })
        : undefined,
  };
};

export const generateConditionLabel = (condition?: FilterOption) => {
  if (!condition) {
    return null;
  }

  if (condition.name !== CUSTOM) {
    return condition.name;
  } else {
    /* i18next-extract-mark-context-next-line  ["above", "below", "between", "crosses_above", "crosses_below", "falls_by", "falls_by_level", "rises_by", "rises_by_level"] */
    return t(`scenarioCommander:domino.conditions_short`, {
      context: condition?.value.condition,
      ...formatInterpolations(condition),
    });
  }
};
